import React from 'react';

import { AxiosError } from 'axios';
import { createColumnHelper } from '@tanstack/react-table';
import throttle from 'lodash/throttle';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { LIST_EVENTS_RESOURCE, useListEvents } from 'src/features/eventLog/api/useListEvents';
import CellEventLogMessage from 'src/features/eventLog/components/cell/CellEventLogMessage';
import CellEventCustomer from 'src/features/eventLog/components/cell/CellEventCustomer';
import { eventTypeBadgeOptions } from 'src/features/eventLog/utils/constants';
import { eventMessages } from 'src/features/eventLog/utils/messages';
import { EventLog } from 'src/features/eventLog/types';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import usePagination from 'src/registry/hooks/usePagination';
import useSorting from 'src/registry/hooks/useSorting';
import { commonMessages } from 'src/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import useSearch from 'src/registry/hooks/useSearch';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import WSContext, { WebSocketGroup } from 'src/features/visitor/context/WSContext';
import { WebSocketEvent } from 'src/features/visitor/types';
import { NEW_STAY_AND_PASS_EVENTS } from 'src/features/visitor/utils/wsEvents';
import { queryClient } from 'src/lib/react-query';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import SidePanel from 'src/registry/components/sidePanel/SidePanel';
import ActiveStaysSidePanel from 'src/features/stay/components/sidePanel/ActiveStaysSidePanel';
import PastStaysSidePanel from 'src/features/stay/components/sidePanel/PastStaysSidePanel';
import { ACTIVE_STAYS_PANEL_NAME } from 'src/features/stay/components/ActiveStaysTable';
import { PAST_STAYS_PANEL_NAME } from 'src/features/stay/components/PastStaysTable';
import { searchStay, SEARCH_STAY_RESOURCE } from 'src/features/stay/api/useSearchStay';
import { searchVisitor, SEARCH_VISITOR_RESOURCE } from 'src/features/visitor/api/useSearchVisitor';
import { PATH } from 'src/features/navigation/utils/constants';
import { toast } from 'src/hooks/UseToast';
import { generalErrorMessages } from 'src/registry/utils/messages';

export default function EventLogTable() {
    const { formatMessage } = useIntl();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting({ initialSortingState: [{ id: 'timestamp', desc: true }] });
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters();

    const { subscribe, unsubscribe } = React.useContext(WSContext);

    React.useEffect(() => {
        const handleWebSocket = throttle((event: WebSocketEvent) => {
            if (NEW_STAY_AND_PASS_EVENTS.includes(event.message)) {
                void queryClient.invalidateQueries({
                    queryKey: [LIST_EVENTS_RESOURCE],
                });
            }
        }, 500);
        subscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);

        return () => {
            handleWebSocket.cancel();
            unsubscribe(WebSocketGroup.GATEWAY_PASS, handleWebSocket);
        };
    }, [subscribe, unsubscribe]);

    const { parkingId } = useParking();

    const {
        data: eventLogData,
        isError,
        isLoading,
    } = useListEvents({
        parkingId,
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...querySearchOptions,
        ...queryFiltersOptions,
    });

    const [defaultDateFilter] = React.useState(() => {
        const now = Date.now();

        return {
            afterDate: new Date(now - 30 * 24 * 60 * 60 * 1000),
            beforeDate: new Date(),
        };
    });

    const navigate = useNavigate();

    const { openPanel } = useSidePanel();
    const [selectedStay, setSelectedStay] = React.useState<{ id: string; isActive: boolean } | null>(null);

    // Resolve the visitor by the identifier value shown in the customer column and open its dashboard.
    // Used for events that only carry an identifier (e.g. a license plate) without a visitor id
    const handleIdentifierClick = async (identifier: string) => {
        try {
            const visitors = await queryClient.fetchQuery({
                queryKey: [SEARCH_VISITOR_RESOURCE, parkingId, identifier],
                queryFn: () => searchVisitor({ parkingId, search: identifier }),
            });
            const visitorId = visitors.results?.[0]?.id;
            if (!visitorId) {
                toast({
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: formatMessage(eventMessages.visitorNotFound),
                    variant: 'destructive',
                });
                return;
            }
            navigate(`/${PATH.VISITORS}/visitor/${visitorId}`);
        } catch (error) {
            // Show the backend's message for client errors (4xx); for server errors (5xx) or no
            // response keep the generic message
            const response = (error as AxiosError<string>).response;
            const description =
                response && response.status < 500 && typeof response.data === 'string' && response.data
                    ? response.data
                    : formatMessage(generalErrorMessages.unknownError);
            toast({
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description,
                variant: 'destructive',
            });
        }
    };

    // Search the stay by ref (active first, then past) and open the matching side panel
    const handleRefClick = async (ref: string) => {
        try {
            const stay = await queryClient.fetchQuery({
                queryKey: [SEARCH_STAY_RESOURCE, parkingId, ref],
                queryFn: () => searchStay({ parkingId, ref }),
            });
            const isActive = stay.exit_time === null;
            setSelectedStay({ id: stay.id, isActive });
            openPanel(isActive ? ACTIVE_STAYS_PANEL_NAME : PAST_STAYS_PANEL_NAME);
        } catch (error) {
            // Show the backend's message for client errors (4xx, e.g. "stay not found"); for server
            // errors (5xx) or no response keep the generic message
            const response = (error as AxiosError<string>).response;
            const description =
                response && response.status < 500 && typeof response.data === 'string' && response.data
                    ? response.data
                    : formatMessage(generalErrorMessages.unknownError);
            toast({
                title: formatMessage(generalErrorMessages.somethingWentWrong),
                description,
                variant: 'destructive',
            });
        }
    };

    const columnHelper = createColumnHelper<EventLog>();

    const columns = [
        columnHelper.accessor('timestamp', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(commonMessages.time),
                inputType: INPUT_TYPE.DATE,
                dateFilter: defaultDateFilter,
            },
            maxSize: 120,
        }),
        columnHelper.accessor('event_type', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(eventMessages.type),
                badgeOptions: eventTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('event_data', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => (
                <CellEventCustomer
                    eventData={row.original.event_data}
                    event={row.original}
                    onIdentifierClick={handleIdentifierClick}
                />
            ),
            meta: {
                title: formatMessage(commonMessages.customer),
            },
        }),
        columnHelper.accessor('id', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => (
                <CellEventLogMessage
                    eventData={row.original.event_data}
                    event={row.original}
                    onRefClick={handleRefClick}
                />
            ),
            meta: {
                title: formatMessage(eventMessages.message),
            },
        }),
    ];

    return (
        <>
            <DataTable<EventLog, any>
                isLoading={isLoading}
                isError={isError}
                columns={columns}
                data={eventLogData}
                pagination={pagination}
                sorting={sorting}
                search={search}
                filters={filters}
                semantics={{
                    title: formatMessage(eventMessages.eventLog),
                    entryName: formatMessage(eventMessages.eventLog).toLowerCase(),
                    entryNames: formatMessage(eventMessages.eventLogs).toLowerCase(),
                }}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
                enableLocalFilters={false}
            />
            <SidePanel onClose={() => setSelectedStay(null)} name={ACTIVE_STAYS_PANEL_NAME}>
                <ActiveStaysSidePanel selectedStayId={selectedStay?.isActive ? selectedStay.id : undefined} />
            </SidePanel>
            <SidePanel onClose={() => setSelectedStay(null)} name={PAST_STAYS_PANEL_NAME}>
                <PastStaysSidePanel
                    selectedStayId={selectedStay && !selectedStay.isActive ? selectedStay.id : undefined}
                />
            </SidePanel>
        </>
    );
}
